'use client';

import { CatCheckboxField } from '../../blocks-components';
import { useTranslations } from 'next-intl';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { TextLink } from 'components/pcc-components';
import Image from 'next/image';
import styles from './equipment-selected.module.scss';
import cx from 'classnames';
import { camelCase, startCase } from 'lodash';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { useToggleFilterByEquipment } from '../common';

const EquipmentSelected = ({
  equipmentSelected,
  shouldTriggerNER
}: {
  equipmentSelected: Equipment;
  shouldTriggerNER: boolean;
}) => {
  const t = useTranslations();
  const {
    editEquipmentDrawer: { openDrawer: openEditEquipmentDrawer }
  } = useMyEquipmentDrawer();

  const { handleCheckboxChange, filterByEquipment } =
    useToggleFilterByEquipment(equipmentSelected);

  const shouldFilterByEquipment = !shouldTriggerNER && filterByEquipment;
  const equipmentName = `${equipmentSelected.model} ${startCase(
    camelCase(equipmentSelected.productFamily)
  )}`;
  const imgURL = equipmentSelected.imageURL + '?wid=64&hei=64&fmt=webp';

  const checkBoxLabel = replaceTokensWithComponents(
    t('REFINE_RESULTS_FOR', { '0': '{0}' }),
    <strong>{equipmentSelected.model}</strong>
  );

  const addSerialLink = replaceTokensWithComponents(
    t('MEQ_TO_GET_AN_EXACT_FIT', { '0': '{0}' }),
    <TextLink
      label={t('MEQ_ADD_SERIAL_NUMBER')}
      onClick={openEditEquipmentDrawer}
      className="text-decoration-underline"
    />
  );

  return (
    <div className="d-flex flex-column py-4 my-2">
      <div className="d-flex">
        <Image
          src={imgURL}
          alt={equipmentName}
          loading="lazy"
          className={cx(
            'd-none d-md-block me-2',
            styles['equipment-widget__equipment-img']
          )}
          width={64}
          height={64}
          unoptimized
        />
        <div className="d-flex flex-column justify-content-center">
          {equipmentSelected.assetName && (
            <>
              <p
                className={cx(
                  'cat-u-theme-typography-display-sm mb-0 d-none d-md-block',
                  styles['equipment-widget__equipment-name']
                )}
              >
                {equipmentSelected.assetName}
              </p>

              <p
                className={cx(
                  'cat-u-theme-typography-headline mb-0 d-md-none',
                  styles['equipment-widget__equipment-name']
                )}
              >
                {equipmentSelected.assetName}
              </p>
            </>
          )}
          <p
            className={cx(
              'cat-u-theme-typography-display-sm mb-0 d-none d-md-block',
              styles['equipment-widget__equipment-name']
            )}
          >
            {equipmentName}
          </p>

          <p
            className={cx(
              'cat-u-theme-typography-title-sm mb-0 d-md-none',
              styles['equipment-widget__equipment-name']
            )}
          >
            {equipmentName}
          </p>
          {equipmentSelected.serialNumber && (
            <>
              <p
                className={cx(
                  'cat-u-theme-typography-body-sm mb-0 d-none d-md-block',
                  styles['equipment-widget__equipment-serial-number']
                )}
              >
                {equipmentSelected.serialNumber}
              </p>

              <p
                className={cx(
                  'mt-1 cat-u-theme-typography-body mb-0 d-md-none',
                  styles['equipment-widget__equipment-serial-number']
                )}
              >
                {equipmentSelected.serialNumber}
              </p>
            </>
          )}
        </div>
      </div>
      <div
        className={cx(
          'd-flex flex-column pt-2',
          styles['equipment-widget__equipment-fields']
        )}
      >
        <CatCheckboxField
          onBlCheckboxChange={handleCheckboxChange}
          checked={shouldFilterByEquipment}
          data-testid="checkboxField"
          className={styles['equipment-widget__equipment-checkbox']}
        >
          <label className="d-block cat-u-theme-typography-body-sm">
            {checkBoxLabel}
          </label>
        </CatCheckboxField>
        {!equipmentSelected.serialNumber && (
          <p className="cat-u-theme-typography-body-sm mb-0">{addSerialLink}</p>
        )}
      </div>
    </div>
  );
};

export default EquipmentSelected;
